import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  Flame,
  Loader2,
  MessageCircle,
  Megaphone,
  Pencil,
  Play,
  Plus,
  Send,
  Sparkles,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const STATUS_OPTIONS = [
  "",
  "New",
  "Contacted",
  "Follow-up",
  "Zoom Done",
  "Visited",
  "Docs Recd",
  "Enrolled",
  "Closed-lost",
  "Wrong number",
];
const TEMPERATURE_OPTIONS = ["", "Hot", "Warm", "Cool", "Cold"];
const TEST_STATUS = ["", "pending", "in_progress", "completed"];
const SOURCE = ["", "public", "manual", "csv_import"];
const PARENT = ["", "high", "medium", "low"];
const BUDGET = ["", "under_5l", "5_10l", "10_20l", "above_20l"];

const emptyFilters = {
  status: "",
  lead_temperature: "",
  preferred_country: "",
  parent_involvement: "",
  budget_band: "",
  test_status: "",
  source_channel: "",
  neet_min: "",
  neet_max: "",
  score_min: "",
};

const emptyForm = {
  name: "",
  description: "",
  whatsapp_template:
    "Hi {first_name}! 👋 Quick check from your MBBS abroad team — got 2 minutes to discuss your {country} options?",
  filters: { ...emptyFilters },
};

function FiltersSummary({ filters }) {
  const items = [];
  if (filters.status) items.push(`Status: ${filters.status}`);
  if (filters.lead_temperature) items.push(`Temp: ${filters.lead_temperature}`);
  if (filters.preferred_country) items.push(`Country: ${filters.preferred_country}`);
  if (filters.test_status) items.push(`Test: ${filters.test_status}`);
  if (filters.parent_involvement) items.push(`Parent: ${filters.parent_involvement}`);
  if (filters.budget_band) items.push(`Budget: ${filters.budget_band.replace("_", "-")}`);
  if (filters.source_channel) items.push(`Source: ${filters.source_channel}`);
  if (filters.neet_min || filters.neet_max)
    items.push(`NEET ${filters.neet_min || 0}-${filters.neet_max || 720}`);
  if (filters.score_min) items.push(`Score ≥ ${filters.score_min}`);
  if (!items.length) return <span className="text-slate-400">All leads</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((t) => (
        <span
          key={t}
          className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700"
        >
          {t}
        </span>
      ))}
    </div>
  );
}

function CampaignFormDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          name: initial.name || "",
          description: initial.description || "",
          whatsapp_template: initial.whatsapp_template || emptyForm.whatsapp_template,
          filters: { ...emptyFilters, ...(initial.filters || {}) },
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [open, initial]);

  const setF = (k, v) => setForm((p) => ({ ...p, filters: { ...p.filters, [k]: v } }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required.");
    setSubmitting(true);
    try {
      const filters = {};
      for (const [k, v] of Object.entries(form.filters)) {
        if (v === "" || v == null) continue;
        if (["neet_min", "neet_max", "score_min"].includes(k)) {
          const n = Number(v);
          if (!Number.isNaN(n)) filters[k] = n;
        } else {
          filters[k] = v;
        }
      }
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        filters,
        whatsapp_template: form.whatsapp_template.trim() || null,
      };
      if (isEdit) {
        await api.patch(`/admin/campaigns/${initial.id}`, payload);
        toast.success("Campaign updated.");
      } else {
        await api.post("/admin/campaigns", payload);
        toast.success("Campaign created.");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="campaign-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-[#0A2540]" />
            {isEdit ? "Edit campaign" : "New campaign"}
          </DialogTitle>
          <DialogDescription>
            Save a filter set + WhatsApp message template you can re-run weekly.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="cmp-name">Campaign name *</Label>
              <Input id="cmp-name" required minLength={2}
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Hot Punjab leads · weekly nudge"
                data-testid="cmp-name" />
            </div>
            <div>
              <Label htmlFor="cmp-desc">Description</Label>
              <Input id="cmp-desc"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Optional"
                data-testid="cmp-desc" />
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50/50 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
              Lead filters
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={form.filters.status || "any"} onValueChange={(v) => setF("status", v === "any" ? "" : v)}>
                  <SelectTrigger className="h-9" data-testid="cmp-f-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">— Any —</SelectItem>
                    {STATUS_OPTIONS.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Temperature</Label>
                <Select value={form.filters.lead_temperature || "any"} onValueChange={(v) => setF("lead_temperature", v === "any" ? "" : v)}>
                  <SelectTrigger className="h-9" data-testid="cmp-f-temp"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">— Any —</SelectItem>
                    {TEMPERATURE_OPTIONS.filter(Boolean).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Country</Label>
                <Input className="h-9" placeholder="e.g. Russia"
                  value={form.filters.preferred_country}
                  onChange={(e) => setF("preferred_country", e.target.value)}
                  data-testid="cmp-f-country" />
              </div>
              <div>
                <Label className="text-xs">Test status</Label>
                <Select value={form.filters.test_status || "any"} onValueChange={(v) => setF("test_status", v === "any" ? "" : v)}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">— Any —</SelectItem>
                    {TEST_STATUS.filter(Boolean).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Parent</Label>
                <Select value={form.filters.parent_involvement || "any"} onValueChange={(v) => setF("parent_involvement", v === "any" ? "" : v)}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">— Any —</SelectItem>
                    {PARENT.filter(Boolean).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Budget</Label>
                <Select value={form.filters.budget_band || "any"} onValueChange={(v) => setF("budget_band", v === "any" ? "" : v)}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">— Any —</SelectItem>
                    {BUDGET.filter(Boolean).map((t) => (
                      <SelectItem key={t} value={t}>{t.replace("_", "-").replace("l", "L")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Source</Label>
                <Select value={form.filters.source_channel || "any"} onValueChange={(v) => setF("source_channel", v === "any" ? "" : v)}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">— Any —</SelectItem>
                    {SOURCE.filter(Boolean).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">NEET min</Label>
                <Input className="h-9" type="number" min="0" max="720"
                  value={form.filters.neet_min}
                  onChange={(e) => setF("neet_min", e.target.value)}
                  data-testid="cmp-f-neet-min" />
              </div>
              <div>
                <Label className="text-xs">NEET max</Label>
                <Input className="h-9" type="number" min="0" max="720"
                  value={form.filters.neet_max}
                  onChange={(e) => setF("neet_max", e.target.value)}
                  data-testid="cmp-f-neet-max" />
              </div>
              <div>
                <Label className="text-xs">Score ≥</Label>
                <Input className="h-9" type="number" min="0" max="100"
                  value={form.filters.score_min}
                  onChange={(e) => setF("score_min", e.target.value)}
                  data-testid="cmp-f-score-min" />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="cmp-tpl">WhatsApp template</Label>
            <Textarea id="cmp-tpl" rows={3}
              value={form.whatsapp_template}
              onChange={(e) => setForm((p) => ({ ...p, whatsapp_template: e.target.value }))}
              placeholder="Hi {first_name}! Quick check on your {country} application…"
              data-testid="cmp-tpl" />
            <p className="mt-1 text-[11px] text-slate-500">
              Variables: {"{name}"} · {"{first_name}"} · {"{country}"} · {"{neet}"} · {"{score}"} · {"{tier}"} · {"{amount}"}
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting}
              className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]" data-testid="cmp-save">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : isEdit ? "Save changes" : "Create campaign"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RunDrawer({ open, onOpenChange, campaign, onRan }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [action, setAction] = useState("whatsapp");
  const [scheduleAt, setScheduleAt] = useState("");
  const [scheduleNote, setScheduleNote] = useState("");
  const [statusTo, setStatusTo] = useState("Contacted");
  const [tplOverride, setTplOverride] = useState("");

  useEffect(() => {
    if (!open || !campaign) return;
    setLoading(true);
    setSelected(new Set());
    setTplOverride("");
    api
      .get(`/admin/campaigns/${campaign.id}/preview`)
      .then(({ data }) => {
        setPreview(data);
        // Pre-select all by default
        setSelected(new Set(data.leads.map((l) => l.id)));
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [open, campaign]);

  const toggle = (id) => {
    setSelected((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (!preview) return;
    setSelected((p) => {
      if (p.size === preview.leads.length) return new Set();
      return new Set(preview.leads.map((l) => l.id));
    });
  };

  const onRun = async () => {
    if (!campaign) return;
    if (selected.size === 0) return toast.error("Select at least one lead.");
    setRunning(true);
    try {
      const body = {
        action,
        lead_ids: Array.from(selected),
      };
      if (action === "schedule_call") {
        if (!scheduleAt) {
          setRunning(false);
          return toast.error("Pick a date/time.");
        }
        body.schedule_at = new Date(scheduleAt).toISOString();
        body.schedule_note = scheduleNote || null;
      }
      if (action === "status_update") body.status_to = statusTo;
      if (action === "whatsapp" && tplOverride.trim()) body.template_override = tplOverride.trim();

      const r = await api.post(`/admin/campaigns/${campaign.id}/run`, body);
      const out = r.data;

      if (action === "whatsapp") {
        // Open up to 10 wa.me tabs
        const tabs = (out.actions || []).slice(0, 10);
        toast.success(`Opening ${tabs.length} WhatsApp tabs…`);
        for (const a of tabs) {
          window.open(a.wa_url, "_blank", "noopener,noreferrer");
          await new Promise((r) => setTimeout(r, 100)); // gentle stagger
        }
      } else if (action === "schedule_call") {
        toast.success(`Scheduled call for ${out.executed_count} leads.`);
      } else if (action === "status_update") {
        toast.success(`Updated ${out.executed_count} leads to ${statusTo}.`);
      }
      onRan?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto px-6 sm:max-w-2xl"
        data-testid="campaign-run-drawer"
      >
        <SheetHeader>
          <SheetTitle className="font-display tracking-tight">
            Run · {campaign?.name}
          </SheetTitle>
          <SheetDescription>
            Pick leads, choose an action, and execute. WhatsApp opens up to 10 tabs at once.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="mt-8 flex items-center justify-center text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading leads…
          </div>
        )}

        {!loading && preview && (
          <>
            <div className="mt-4 flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/50 p-3">
              <span className="text-sm font-medium text-[#0A2540]">
                {preview.match_count} matches · {selected.size} selected
              </span>
              <Button variant="ghost" size="sm" onClick={toggleAll} data-testid="campaign-toggle-all">
                {selected.size === preview.leads.length ? "Clear" : "Select all"}
              </Button>
            </div>

            <div className="mt-3 max-h-72 overflow-auto rounded-md border border-slate-200">
              {preview.leads.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-500">
                  No leads match this campaign right now.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead className="text-right">Score</TableHead>
                      <TableHead className="text-right">Temp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.leads.map((l) => (
                      <TableRow
                        key={l.id}
                        className="cursor-pointer"
                        onClick={() => toggle(l.id)}
                        data-testid={`campaign-lead-${l.id}`}
                      >
                        <TableCell>
                          <input type="checkbox" checked={selected.has(l.id)}
                            onChange={() => toggle(l.id)} className="h-3.5 w-3.5" />
                        </TableCell>
                        <TableCell className="font-medium text-[#0A2540]">{l.name}</TableCell>
                        <TableCell className="text-slate-600">{l.preferred_country}</TableCell>
                        <TableCell className="text-right tabular-nums">{l.lead_score}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary"
                            className={
                              l.lead_temperature === "Hot" ? "bg-rose-100 text-rose-700" :
                              l.lead_temperature === "Warm" ? "bg-amber-100 text-amber-700" :
                              l.lead_temperature === "Cool" ? "bg-blue-100 text-blue-700" :
                              "bg-slate-100 text-slate-600"
                            }>
                            {l.lead_temperature}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            <div className="mt-5">
              <Label className="text-xs uppercase tracking-[0.14em] text-slate-600">Action</Label>
              <div className="mt-2 grid grid-cols-3 gap-2" data-testid="campaign-action-tabs">
                {[
                  { v: "whatsapp", l: "WhatsApp", icon: MessageCircle },
                  { v: "schedule_call", l: "Schedule call", icon: Calendar },
                  { v: "status_update", l: "Update status", icon: Sparkles },
                ].map((a) => {
                  const Icon = a.icon;
                  const active = action === a.v;
                  return (
                    <button
                      key={a.v}
                      onClick={() => setAction(a.v)}
                      className={`flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
                        active ? "border-[#0A2540] bg-[#0A2540] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-[#0A2540]"
                      }`}
                      data-testid={`campaign-action-${a.v}`}
                    >
                      <Icon className="h-3.5 w-3.5" /> {a.l}
                    </button>
                  );
                })}
              </div>

              {action === "whatsapp" && (
                <div className="mt-3">
                  <Label className="text-xs">Template (override default)</Label>
                  <Textarea rows={3}
                    value={tplOverride}
                    onChange={(e) => setTplOverride(e.target.value)}
                    placeholder={campaign?.whatsapp_template || "(uses campaign default)"}
                    data-testid="campaign-tpl-override" />
                </div>
              )}
              {action === "schedule_call" && (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs">Scheduled at</Label>
                    <Input type="datetime-local" value={scheduleAt}
                      onChange={(e) => setScheduleAt(e.target.value)}
                      data-testid="campaign-schedule-at" />
                  </div>
                  <div>
                    <Label className="text-xs">Note</Label>
                    <Input value={scheduleNote}
                      onChange={(e) => setScheduleNote(e.target.value)}
                      placeholder="Bulk follow-up"
                      data-testid="campaign-schedule-note" />
                  </div>
                </div>
              )}
              {action === "status_update" && (
                <div className="mt-3">
                  <Label className="text-xs">Move all selected to</Label>
                  <Select value={statusTo} onValueChange={setStatusTo}>
                    <SelectTrigger data-testid="campaign-status-to"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.filter(Boolean).map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pb-10">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={onRun}
                disabled={running || selected.size === 0}
                className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
                data-testid="campaign-run-btn"
              >
                {running ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Running…</> : <><Play className="mr-1 h-4 w-4" /> Run on {selected.size} leads</>}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function CampaignsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [runOpen, setRunOpen] = useState(false);
  const [running, setRunning] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/campaigns");
      setCampaigns(r.data.campaigns || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totalMatches = useMemo(
    () => campaigns.reduce((s, c) => s + (c.match_count || 0), 0),
    [campaigns],
  );

  if (user && user.role !== "admin" && user.role !== "super_admin") {
    nav("/admin", { replace: true });
    return null;
  }

  const onDelete = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/campaigns/${c.id}`);
      toast.success("Campaign deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onEdit = (c) => {
    setEditing(c);
    setFormOpen(true);
  };
  const onNew = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const onRun = (c) => {
    setRunning(c);
    setRunOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-50/60">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 pl-16 sm:px-6 md:pl-6 lg:px-10">
          <div className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-[#0A2540]" />
            <span className="font-display text-base font-bold tracking-tight text-[#0A2540]">
              Campaigns
            </span>
          </div>
          <Button
            className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
            onClick={onNew}
            data-testid="campaign-new-btn"
          >
            <Plus className="mr-1.5 h-4 w-4" /> New campaign
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A2540]">
              Saved Campaigns
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Re-runnable filter sets + WhatsApp templates · {campaigns.length} saved · {totalMatches} matched leads right now
            </p>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20 text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading campaigns…
          </div>
        )}

        {!loading && campaigns.length === 0 && (
          <div className="mt-10 flex flex-col items-center rounded-xl border border-dashed border-slate-200 bg-white py-16 text-center" data-testid="campaigns-empty">
            <Megaphone className="h-12 w-12 text-slate-300" />
            <h3 className="font-display mt-3 text-lg font-semibold text-[#0A2540]">
              No campaigns yet
            </h3>
            <p className="mt-1 max-w-md text-sm text-slate-500">
              Save a filter set as a campaign — e.g. "Hot Punjab leads who took the test" — and re-run it weekly with one click.
            </p>
            <Button onClick={onNew} className="mt-5 bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
              data-testid="campaign-empty-new-btn">
              <Plus className="mr-1.5 h-4 w-4" /> Create first campaign
            </Button>
          </div>
        )}

        {!loading && campaigns.length > 0 && (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {campaigns.map((c) => (
              <div
                key={c.id}
                className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-sm"
                data-testid={`campaign-card-${c.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-display truncate text-base font-semibold text-[#0A2540]">
                      {c.name}
                    </h3>
                    {c.description && (
                      <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{c.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => onEdit(c)} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-[#0A2540]"
                      title="Edit" data-testid={`campaign-edit-${c.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => onDelete(c)} className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                      title="Delete" data-testid={`campaign-delete-${c.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                <div className="mt-3">
                  <FiltersSummary filters={c.filters || {}} />
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3 text-center">
                  <div>
                    <div className="font-display text-xl font-bold tabular-nums text-[#0A2540]">
                      {c.match_count ?? "—"}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Matches</div>
                  </div>
                  <div>
                    <div className="font-display text-xl font-bold tabular-nums text-[#0A2540]">
                      {c.total_runs || 0}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Runs</div>
                  </div>
                  <div>
                    <div className="font-display text-xl font-bold tabular-nums text-[#0A2540]">
                      {c.last_run_count || 0}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Last run</div>
                  </div>
                </div>

                <Button
                  className="mt-4 w-full bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
                  size="sm"
                  onClick={() => onRun(c)}
                  disabled={!c.match_count}
                  data-testid={`campaign-run-${c.id}`}
                >
                  <Play className="mr-1.5 h-4 w-4" /> Run
                </Button>

                {c.last_run_at && (
                  <div className="mt-2 flex items-center justify-center gap-1 text-[10px] text-slate-400">
                    <Clock className="h-3 w-3" /> Last run {new Date(c.last_run_at).toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      <CampaignFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={editing}
        onSaved={load}
      />
      <RunDrawer
        open={runOpen}
        onOpenChange={setRunOpen}
        campaign={running}
        onRan={load}
      />
    </div>
  );
}
